import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Plus, Loader2, Trash2, Pencil, Eye, EyeOff, X, Upload, FileText } from 'lucide-react';

const SUPPORT_TYPES = ['CV Optimization', 'Job Search Strategy', 'Interview Preparation', 'Career Support'];

const EMPTY_FORM = {
  display_name: '', job_title: '', company: '', date_secured: '',
  story: '', support_type: SUPPORT_TYPES[0], screenshot_url: '',
  proof_data_url: '', proof_mime: '', proof_filename: '',
  social_url: '', published: false,
};

export const AdminSuccessStories = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error('File too large (max 5 MB)'); return; }
    const fd = new FormData();
    fd.append('file', f);
    setUploading(true);
    try {
      const res = await axios.post(`${API}/admin/success-stories/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(prev => ({ ...prev, proof_data_url: res.data.data_url, proof_mime: res.data.mime, proof_filename: res.data.filename }));
      toast.success('Proof uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); e.target.value = ''; }
  };

  const clearUpload = () => setForm(prev => ({ ...prev, proof_data_url: '', proof_mime: '', proof_filename: '' }));

  const fetchStories = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/success-stories`);
      setStories(res.data.stories || []);
    } catch { toast.error('Failed to load success stories'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchStories(); }, []);

  const openNew = () => { setForm(EMPTY_FORM); setEditingId(null); setShowForm(true); };
  const openEdit = (s) => { setForm({ ...EMPTY_FORM, ...s }); setEditingId(s.id); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditingId(null); setForm(EMPTY_FORM); };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.display_name.trim() || !form.job_title.trim() || !form.story.trim()) {
      toast.error('Name, job title and story are required'); return;
    }
    setSaving(true);
    try {
      if (editingId) {
        const res = await axios.patch(`${API}/admin/success-stories/${editingId}`, form);
        toast.success('Story updated');
      } else {
        await axios.post(`${API}/admin/success-stories`, form);
        toast.success('Story created');
      }
      closeForm();
      fetchStories();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save story'); }
    finally { setSaving(false); }
  };

  const handleTogglePublish = async (s) => {
    try {
      await axios.patch(`${API}/admin/success-stories/${s.id}`, { ...s, published: !s.published });
      toast.success(s.published ? 'Story unpublished' : 'Story published');
      fetchStories();
    } catch { toast.error('Failed to update'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this success story? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/success-stories/${id}`);
      toast.success('Story deleted');
      fetchStories();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <Card className="p-6" data-testid="admin-success-stories-section">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Success Stories ({stories.length})</h2>
        <Button size="sm" className="btn-uae-red" onClick={openNew} data-testid="add-story-btn">
          <Plus className="w-4 h-4 mr-1" /> Add New Story
        </Button>
      </div>

      {showForm && (
        <form onSubmit={handleSave} className="border rounded-lg p-4 mb-6 space-y-3 bg-gray-50" data-testid="story-form">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold text-sm">{editingId ? 'Edit Story' : 'New Story'}</h3>
            <button type="button" onClick={closeForm}><X className="w-4 h-4 text-gray-500" /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <input placeholder="Candidate Display Name *" value={form.display_name}
              onChange={e => setForm({ ...form, display_name: e.target.value })}
              className="h-9 rounded border border-gray-300 px-3 text-sm" data-testid="story-display-name-input" />
            <input placeholder="Job Title *" value={form.job_title}
              onChange={e => setForm({ ...form, job_title: e.target.value })}
              className="h-9 rounded border border-gray-300 px-3 text-sm" data-testid="story-job-title-input" />
            <input placeholder="Company (optional)" value={form.company}
              onChange={e => setForm({ ...form, company: e.target.value })}
              className="h-9 rounded border border-gray-300 px-3 text-sm" data-testid="story-company-input" />
            <input type="date" placeholder="Job Secured Date" value={form.date_secured}
              onChange={e => setForm({ ...form, date_secured: e.target.value })}
              className="h-9 rounded border border-gray-300 px-3 text-sm" data-testid="story-date-input" />
            <select value={form.support_type} onChange={e => setForm({ ...form, support_type: e.target.value })}
              className="h-9 rounded border border-gray-300 px-3 text-sm" data-testid="story-support-type-select">
              {SUPPORT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <input placeholder="Screenshot / Image URL (optional)" value={form.screenshot_url}
              onChange={e => setForm({ ...form, screenshot_url: e.target.value })}
              className="h-9 rounded border border-gray-300 px-3 text-sm" data-testid="story-screenshot-url-input" />
            <input placeholder="Social Media Post URL (optional)" value={form.social_url}
              onChange={e => setForm({ ...form, social_url: e.target.value })}
              className="h-9 rounded border border-gray-300 px-3 text-sm sm:col-span-2" data-testid="story-social-url-input" />
          </div>

          {/* Proof file upload (JPG/PNG/WEBP/PDF, max 5MB) */}
          <div className="border border-dashed rounded-md p-3 bg-white">
            <label className="block text-xs font-medium text-gray-700 mb-2">Upload proof (WhatsApp screenshot, offer letter, PDF — JPG/PNG/WEBP/PDF, max 5 MB)</label>
            {form.proof_data_url ? (
              <div className="flex items-center gap-3">
                {form.proof_mime?.startsWith('image/') ? (
                  <img src={form.proof_data_url} alt="proof preview" className="w-20 h-20 object-cover rounded border" data-testid="story-proof-image-preview" />
                ) : (
                  <div className="w-20 h-20 flex flex-col items-center justify-center border rounded bg-gray-50" data-testid="story-proof-pdf-preview">
                    <FileText className="w-6 h-6 text-red-500" />
                    <span className="text-[10px] mt-1">PDF</span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-xs truncate">{form.proof_filename || 'uploaded proof'}</p>
                  <p className="text-[10px] text-gray-400">{form.proof_mime}</p>
                </div>
                <button type="button" onClick={clearUpload} className="text-red-500 text-xs" data-testid="clear-proof-btn">Remove</button>
              </div>
            ) : (
              <label className="flex items-center gap-2 cursor-pointer text-sm text-[#CE1126]">
                {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4" /> Choose file</>}
                <input type="file" accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf" className="hidden" onChange={handleFileUpload} disabled={uploading} data-testid="story-proof-upload-input" />
              </label>
            )}
          </div>
          <textarea placeholder="Short Success Story / Context *" value={form.story} rows={3}
            onChange={e => setForm({ ...form, story: e.target.value })}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm" data-testid="story-text-input" />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.published} onChange={e => setForm({ ...form, published: e.target.checked })}
              className="accent-[#CE1126]" data-testid="story-published-checkbox" />
            Published (visible on public Success Stories page)
          </label>
          <Button type="submit" className="btn-uae-red" disabled={saving} data-testid="story-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingId ? 'Update Story' : 'Create Story')}
          </Button>
        </form>
      )}

      {loading ? <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div> : (
        <div className="space-y-2">
          {stories.map(s => (
            <div key={s.id} className="flex items-center justify-between border rounded-lg p-3 text-sm" data-testid={`admin-story-row-${s.id}`}>
              <div>
                <p className="font-medium">{s.display_name} — {s.job_title}{s.company && ` @ ${s.company}`}</p>
                <p className="text-xs text-gray-500">{s.support_type} • {s.date_secured || 'no date'} • <span className={s.published ? 'text-green-600' : 'text-gray-400'}>{s.published ? 'Published' : 'Draft'}</span></p>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => handleTogglePublish(s)} title={s.published ? 'Unpublish' : 'Publish'} data-testid={`toggle-publish-${s.id}`}>
                  {s.published ? <Eye className="w-4 h-4 text-green-600" /> : <EyeOff className="w-4 h-4 text-gray-400" />}
                </button>
                <button onClick={() => openEdit(s)} title="Edit" data-testid={`edit-story-${s.id}`}><Pencil className="w-4 h-4 text-blue-600" /></button>
                <button onClick={() => handleDelete(s.id)} title="Delete" data-testid={`delete-story-${s.id}`}><Trash2 className="w-4 h-4 text-red-500" /></button>
              </div>
            </div>
          ))}
          {stories.length === 0 && <p className="text-center text-gray-400 py-8">No success stories yet.</p>}
        </div>
      )}
    </Card>
  );
};
